import { getPeriodLength } from "../ClockPeriod";
import { convertClock, convertClockWithMs } from "../ConvertClock";
import { toCatchupFilter } from "./ToCatchupFilter";

export async function multiDeviceClockCatchup(messages, currentState, updateState, fixtureProfile) {
  const isPeriodOrClockEvent = (event) => ["period", "fixture", "main"].includes(event.eventType);
  // handle only a new period, fixture or clock events
  if (!toCatchupFilter(messages, currentState).some(isPeriodOrClockEvent)) {
    return;
  }

  // replay the whole history, so a late event from offline device lands in its place, not on top of newer state
  const tempPeriod = { periodId: 0 };
  const tempClock = currentState.clock ? JSON.parse(JSON.stringify(currentState.clock)) : {};

  const setStoppedClock = (clock) => {
    tempClock.clockRunning = false;
    const parsedTime = convertClockWithMs(clock);
    tempClock.displayTime = parsedTime[0];
    tempClock.msCatchup = parseInt(parsedTime[1], 10);
  };

  const setRunningClock = (clock, eventTime) => {
    tempClock.clockRunning = true;
    const deltaTime = new Date().getTime() - new Date(`${eventTime}${eventTime.includes("Z") ? "" : "Z"}`).getTime();
    const deltaSeconds = Math.floor(deltaTime / 1000);
    const deltaMs = deltaTime - deltaSeconds * 1000;
    const parsedTime = convertClockWithMs(clock);
    const splitTime = parsedTime[0].split(":");
    const msTime = parsedTime[1];
    let allSeconds;
    let msCatchup;
    if (fixtureProfile.clockDirection === "COUNT_DOWN") {
      allSeconds = parseInt(splitTime[0], 10) * 60 + parseInt(splitTime[1], 10) - deltaSeconds;
      msCatchup = Math.max(0, Math.min(999, parseInt(msTime, 10) - deltaMs));
    } else {
      allSeconds = parseInt(splitTime[0], 10) * 60 + parseInt(splitTime[1], 10) + deltaSeconds;
      msCatchup = Math.max(0, Math.min(999, parseInt(msTime, 10) + deltaMs));
    }
    const newMins = Math.floor(allSeconds / 60);
    const newSecs = allSeconds % 60;
    tempClock.displayTime = newMins + ":" + newSecs;
    tempClock.msCatchup = msCatchup;
  };

  const toCatchupEvents = messages.filter(isPeriodOrClockEvent).sort(
    // sort by period first
    (a, b) =>
      a.periodId - b.periodId || new Date(a.eventTime.replace("Z", "")) - new Date(b.eventTime.replace("Z", "")),
  );

  toCatchupEvents.forEach((event) => {
    if (event.periodId >= tempPeriod.periodId) {
      tempPeriod.periodId = event.periodId;
    }

    const handlePeriodEvent = () => {
      tempPeriod.periodId = event.periodId;
      switch (event.subType) {
        case "start":
          tempPeriod.periodStatus = "periodStarted";
          break;
        case "end":
          tempPeriod.periodStatus = "periodEnd";
          break;
        case "confirmed":
          if (tempPeriod.periodId >= fixtureProfile.numberOfPeriods) {
            tempPeriod.periodStatus = "pendingExtra";
          } else {
            tempPeriod.periodStatus = "pending";
            tempPeriod.periodId = tempPeriod.periodId + 1;
          }
          break;
        case "pending":
          if (event.periodId > 0) {
            tempPeriod.periodStatus = "pending";
            if (fixtureProfile.clockDirection === "COUNT_DOWN") {
              tempClock.displayTime = convertClock(`PT${getPeriodLength(event.periodId, fixtureProfile)}M0S`);
            } else {
              tempClock.displayTime = convertClock("PT0M0S");
            }
          }
          break;
        default:
          break;
      }
    };

    const handleFixtureEvent = () => {
      switch (event.subType) {
        case "end":
          tempPeriod.periodStatus = "fixtureConfirm";
          break;
        case "confirmed":
          tempPeriod.periodStatus = "fixtureComplete";
          break;
        default:
          break;
      }
    };

    const handleClockMainEvent = () => {
      if (event.class !== "clock") {
        return;
      }

      switch (event.subType) {
        case "stop":
          setStoppedClock(event.clock);
          break;
        case "start":
          setRunningClock(event.clock, event.eventTime);
          break;
        case "adjust":
          if (!event.options?.value) {
            break;
          }
          if (tempClock.clockRunning) {
            setRunningClock(event.options.value, event.eventTime);
          } else {
            setStoppedClock(event.options.value);
          }
          break;
        default:
          break;
      }
    };

    switch (event.eventType) {
      case "period":
        handlePeriodEvent();
        break;
      case "fixture":
        handleFixtureEvent();
        break;
      case "main":
        handleClockMainEvent();
        break;
      default:
        break;
    }
  });

  updateState("periodCatchup", tempPeriod);
  updateState("clockCatchup", tempClock);
}
